import { useNavigate } from 'react-router-dom'

import { useGenerateAuthTokenMutation, useLazyGetRxpEligibilityQuery } from '../modules/flexApi'
import { Config } from '../providers/config'
import { useLogger } from '../providers/logger'
import * as Routes from '../routing/constants'
import { FLEXAPP_AMP_DEVICE_ID } from '../types/local-storage-keys'
import { useEmbed } from './embed'

export const useRxpExperimentSetup = () => {
  const [generateAuthToken, { isLoading: isGeneratingAuthToken }] = useGenerateAuthTokenMutation()
  const embed = useEmbed()
  const navigate = useNavigate()
  const [getRxpEligibilityQuery, { isLoading: isRxpEligibilityLoading }] = useLazyGetRxpEligibilityQuery()
  const logger = useLogger('RxpEligibilityWrapper')

  /**
   * Check if the customer is eligible for RXP
   * @param customerId - The customer ID
   * @param isNewCustomer - Whether the customer is a new customer
   * @param isEmbed - Whether the customer is an embed, currently embed is not eligible
   * @returns True if the customer is eligible for RXP, false otherwise
   */
  const checkRxpEligibility = async (customerId: string, isNewCustomer?: boolean, isEmbed?: boolean) => {
    const deviceIdOverride = localStorage.getItem(FLEXAPP_AMP_DEVICE_ID)

    try {
      const rxpEligibility = await getRxpEligibilityQuery({
        customerId,
        isNewCustomer: isEmbed ? undefined : isNewCustomer,
        deviceId: deviceIdOverride ? deviceIdOverride : undefined,
      }).unwrap()

      return rxpEligibility.data.eligible
    } catch (error: any) {
      logger.error('Error checking RXP eligibility', error?.message)
      return false
    }
  }

  /**
   * Redirect to RXP experience and persist the smsOptin for RXP usage
   * @param userId - The user ID
   * @param smsNotificationsAccepted - Whether the customer has accepted SMS notifications
   */
  const redirectToRxp = async (userId: string, smsNotificationsAccepted?: boolean) => {
    if (embed.isEmbed) {
      logger.info('User is eligible for RXP but going through embed, redirecting to blocking screen')
      return navigate(Routes.App.BLOCK_RXP, { replace: true })
    }

    try {
      const authToken = await generateAuthToken({
        customerId: userId,
      }).unwrap()

      if (smsNotificationsAccepted) {
        window.location.replace(
          `${Config.RXP_DOMAIN}/auth/delegate?cid=${userId}&sms_opt_in=${smsNotificationsAccepted}&token=${authToken.data.token}`
        )
      } else {
        window.location.replace(`${Config.RXP_DOMAIN}/auth/delegate?cid=${userId}&token=${authToken.data.token}`)
      }
    } catch (error: any) {
      logger.error('Error generating auth token', error?.message)
    }
  }

  return {
    redirectToRxp,
    isGeneratingAuthToken,
    checkRxpEligibility,
    isRxpEligibilityLoading,
  }
}
